import { useEffect, useState, type FormEvent } from "react";
import { Link, Navigate, useNavigate, useParams } from "react-router";
import { BUYER_REASONS, PROBLEM_IMAGES_MAX, SELLER_REASONS } from "../../shared/limits.ts";
import { compressImage } from "../lib/compress.ts";
import { useI18n } from "../lib/i18n.tsx";
import { useMe } from "../lib/me.tsx";
import { loadPurchase, type PurchaseView } from "../lib/purchase-api.ts";

type Problem = {
  id: string;
  reporter_id: string;
  reason: string;
  note: string;
  reply_note: string | null;
  status: string;
  resolution: string | null;
  admin_note: string | null;
  created_at: number;
  replied_at: number | null;
};

/** /p/:id/problem — file (reporter) or reply (other party) once; shows the admin's resolution note. */
export function ProblemPage() {
  const { id = "" } = useParams();
  const { t } = useI18n();
  const { me, loading } = useMe();
  const navigate = useNavigate();
  const [p, setP] = useState<PurchaseView | null>(null);
  const [reason, setReason] = useState<string>("");
  const [note, setNote] = useState("");
  const [files, setFiles] = useState<File[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!me) return;
    let live = true;
    loadPurchase(id)
      .then((v) => live && setP(v))
      .catch(() => live && setP(null));
    return () => {
      live = false;
    };
  }, [id, me]);

  if (loading) return <div className="page muted">{t("common.loading")}</div>;
  if (!me) return <Navigate to={`/login?next=/p/${id}/problem`} replace />;
  if (!p) return <div className="page muted">{t("common.loading")}</div>;
  const problem = p.problem as Problem | null;
  const reasons = p.role === "buyer" ? BUYER_REASONS : SELLER_REASONS;
  const isReporter = problem ? problem.reporter_id === me.id : false;
  const canReply =
    problem && !isReporter && problem.replied_at === null && problem.status === "open";

  async function submit(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const form = new FormData();
      if (!problem) form.set("reason", reason);
      form.set("note", note);
      for (const f of files.slice(0, PROBLEM_IMAGES_MAX))
        form.append("images", await compressImage(f), "image");
      const res = await fetch(`/api/purchases/${id}/problem${problem ? "/reply" : ""}`, {
        method: "POST",
        headers: { "X-Yabe": "1" },
        body: form,
        credentials: "same-origin",
      });
      if (!res.ok) throw new Error(String(res.status));
      navigate(`/p/${id}`);
    } catch {
      setError(t("error.generic"));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="page page--narrow" data-testid="problem-page">
      <h1 className="h1">{t("problem.title")}</h1>
      <p className="muted">{p.listing?.title}</p>
      {problem ? (
        <section className="section" data-testid="problem-existing">
          <p>
            <strong>
              {t(`problem.reason.${problem.reason as (typeof BUYER_REASONS)[number]}`)}
            </strong>
          </p>
          <p>{problem.note}</p>
          {problem.reply_note ? <p className="quote">{problem.reply_note}</p> : null}
          {problem.status === "resolved" ? (
            <p className="ok">{problem.admin_note}</p>
          ) : (
            <p className="muted small">{t("problem.filed")}</p>
          )}
        </section>
      ) : null}
      {!problem || canReply ? (
        <form className="form" onSubmit={submit}>
          {!problem ? (
            <div className="chips">
              {reasons.map((r) => (
                <button
                  key={r}
                  type="button"
                  className={`chip${reason === r ? " is-on" : ""}`}
                  onClick={() => setReason(r)}
                  data-testid={`reason-${r}`}
                >
                  {t(`problem.reason.${r}`)}
                </button>
              ))}
            </div>
          ) : null}
          <label className="field">
            <span className="label">{problem ? t("problem.reply") : t("problem.note")}</span>
            <textarea
              className="input"
              rows={4}
              maxLength={1000}
              value={note}
              onChange={(e) => setNote(e.target.value)}
              data-testid="problem-note"
            />
          </label>
          <label className="field">
            <span className="label">{t("problem.images")}</span>
            <input
              type="file"
              accept="image/*"
              multiple
              onChange={(e) =>
                setFiles(Array.from(e.target.files ?? []).slice(0, PROBLEM_IMAGES_MAX))
              }
            />
          </label>
          {error ? (
            <p className="error" role="alert">
              {error}
            </p>
          ) : null}
          <button
            className="btn btn--accent"
            type="submit"
            disabled={busy || (!problem && !reason) || (!!problem && !note.trim())}
            data-testid="problem-submit"
          >
            {problem ? t("problem.reply") : t("purchase.report_problem")}
          </button>
        </form>
      ) : null}
      <p>
        <Link className="btn btn--ghost" to={`/p/${id}`}>
          {t("common.back")}
        </Link>
      </p>
    </div>
  );
}
